import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Check, ChevronRight, Loader2 } from 'lucide-react';
import client from '../api/client';
import { useTranslation } from 'react-i18next';

const DAY_LABELS = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

export default function ImportDietFromClientSheet({ open, onOpenChange, targetClientId, targetClientName, allClients, existingSuggestions = [], onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importing, setImporting] = useState(false);

  // Reset when sheet opens
  useEffect(() => {
    if (open) {
      setSelectedSource(null);
      setSuggestions([]);
      setSelectedIds([]);
    }
  }, [open]);

  const otherClients = (allClients || []).filter(c => c.id !== targetClientId);

  const pickSource = async (c) => {
    setSelectedSource(c);
    setLoadingSuggestions(true);
    setSelectedIds([]);
    try {
      const res = await client.get(`/diet-suggestions?client_id=${c.id}`);
      setSuggestions(res.data || []);
    } catch {
      toast.error(t('importDiet.failedToLoad'));
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const toggleId = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === suggestions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(suggestions.map(s => s.id));
    }
  };

  const handleImport = async () => {
    if (!selectedIds.length) return;
    setImporting(true);
    try {
      await client.post('/diet-suggestions/import-from-client', {
        source_client_id: selectedSource.id,
        target_client_id: targetClientId,
        suggestion_ids: selectedIds,
      });
      toast.success(selectedIds.length === 1 ? t('importDiet.importedSuccess', { count: selectedIds.length }) : t('importDiet.importedSuccessPlural', { count: selectedIds.length }));
      onSuccess?.();
      onOpenChange(false);
    } catch {
      toast.error(t('importDiet.failedToImport'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80dvh] overflow-y-auto" zIndex={250}>
        <SheetHeader>
          <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {selectedSource
              ? t('importDiet.importFromTitle', { name: selectedSource.name })
              : t('importDiet.importToTitle', { name: targetClientName })}
          </SheetTitle>
        </SheetHeader>

        <div className="py-4 space-y-3">
          {!selectedSource ? (
            /* Step 1: Pick source client */
            otherClients.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('importDiet.noOtherClients')}
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {t('importDiet.selectClientToCopy')}
                </p>
                {otherClients.map(c => (
                  <button
                    key={c.id}
                    onClick={() => pickSource(c)}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted active:bg-muted transition-colors text-left"
                  >
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </>
            )
          ) : (
            /* Step 2: Pick suggestions to import */
            <>
              {loadingSuggestions ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : suggestions.length === 0 ? (
                <>
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('importDiet.noActiveSuggestions')}
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => setSelectedSource(null)}>
                    {t('importDiet.back')}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {t('importDiet.selectSuggestionsInfo', { selected: selectedIds.length, total: suggestions.length })}
                    </p>
                    <Button variant="ghost" size="sm" onClick={toggleAll} className="text-xs h-7">
                      {selectedIds.length === suggestions.length ? t('importDiet.deselectAll') : t('importDiet.selectAll')}
                    </Button>
                  </div>

                  {suggestions.map(s => (
                    <button
                      key={s.id}
                      onClick={() => toggleId(s.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        selectedIds.includes(s.id)
                          ? 'bg-primary/10 border-2 border-primary'
                          : 'bg-muted/50 border-2 border-transparent'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 ${
                        selectedIds.includes(s.id)
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground/30'
                      }`}>
                        {selectedIds.includes(s.id) && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.name}</p>
                        {s.days_of_week?.length > 0 && (
                          <p className="text-xs text-primary font-medium">
                            {s.days_of_week.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(' · ')}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {Math.round(s.total_calories || 0)} kcal · P:{Math.round(s.total_protein || 0)}g · C:{Math.round(s.total_carbs || 0)}g · F:{Math.round(s.total_fat || 0)}g
                        </p>
                      </div>
                    </button>
                  ))}

                  {(() => {
                    const existingDays = new Set();
                    existingSuggestions.forEach(s => (s.days_of_week || []).forEach(d => existingDays.add(d)));
                    const selectedSuggestions = suggestions.filter(s => selectedIds.includes(s.id));
                    const importDays = new Set();
                    selectedSuggestions.forEach(s => (s.days_of_week || []).forEach(d => importDays.add(d)));
                    const overlapping = [...importDays].filter(d => existingDays.has(d));
                    if (overlapping.length === 0 || selectedIds.length === 0) return null;
                    return (
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-yellow-500">
                          {overlapping.map(d => DAY_LABELS[d] || d).join(', ')} already {overlapping.length === 1 ? 'has' : 'have'} a diet suggestion. The client will see multiple plans for {overlapping.length === 1 ? 'that day' : 'those days'}.
                        </p>
                      </div>
                    );
                  })()}

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" className="flex-1" onClick={() => setSelectedSource(null)}>
                      {t('importDiet.back')}
                    </Button>
                    <Button
                      className="flex-1 bg-primary hover:bg-primary/90"
                      onClick={handleImport}
                      disabled={importing || !selectedIds.length}
                    >
                      {importing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      {selectedIds.length === 1 ? t('importDiet.importBtnSingular', { count: selectedIds.length }) : t('importDiet.importBtnPlural', { count: selectedIds.length })}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
